import React from 'react';
import { useTranslation } from 'react-i18next';
import { IMPORT_EXPORT_PROCESS_TYPE, IMPORT_EXPORT_STATUS } from 'Common/enums';
import { DateService } from 'Services';

const useGetSidePanelContentCardHelperData = (data: SidePanelAccordionItemContentCardData) => {
    const [isHelperDataAvailable, setIsHelperDataAvailable] = React.useState<boolean>(false);
    const { t } = useTranslation();
    const status = data.status?.previousStatus?.type ?? data.status.type;

    const isImport = data.entryActionType === IMPORT_EXPORT_PROCESS_TYPE.IMPORT;
    const isFromQueuedSection = status === IMPORT_EXPORT_STATUS.QUEUED || status === IMPORT_EXPORT_STATUS.IN_PROGRESS;

    const isFailed = status === IMPORT_EXPORT_STATUS.FAILED;
    const isCancelled = status === IMPORT_EXPORT_STATUS.CANCELLED;
    const isFinished = status === IMPORT_EXPORT_STATUS.FINISHED;

    const entriesAdded = data.progressDetails?.entriesAdded ?? 0;
    const entriesRemoved = data.progressDetails?.entriesRemoved ?? 0;
    const entriesModified = data.progressDetails?.entriesModified ?? 0;
    const entriesFailed = data.progressDetails?.entriesFailed ?? 0;
    const hasStartedProcessing = entriesAdded > 0 || entriesRemoved > 0 || entriesModified > 0 || entriesFailed > 0;

    const shouldShowHelperData =
        ((isFailed || isCancelled) && hasStartedProcessing) || isFinished || (isImport && isFromQueuedSection);
    const finishedOnDateTime = DateService.getDateTime(data.progressDetails?.finishedOn ?? undefined);

    const getHelperTableProps = React.useCallback(() => {
        let props;
        if (isImport) {
            props = {
                ...(hasStartedProcessing
                    ? {
                          entriesAdded,
                          entriesRemoved,
                          entriesModified,
                          entriesFailed
                      }
                    : undefined),
                importFilename: isFromQueuedSection ? data.fileName : data.progressDetails?.importFilename
            };
        } else {
            props = {
                listEntries: data.progressDetails?.listEntries,
                listType: data.progressDetails?.listType,
                fileInExport: data.progressDetails?.fileInExport,
                ...(isFinished ? { finishedOn: finishedOnDateTime } : undefined)
            };
        }
        return props;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.progressDetails, finishedOnDateTime, isFinished, isImport]);

    const helperChildContentTitle = React.useCallback(() => {
        // Queued Entries
        if (isFromQueuedSection && !isImport) {
            // Title for Export entries
            return t('Larger lists may take a few hours to complete');
        }
        // Completed Entries
        if (isFailed) {
            return data.progressDetails?.errorMessage;
        }
        if (isImport) {
            return t('Import Details');
        }
        if (!isImport && !isFromQueuedSection) {
            return t('Export Details');
        }
        return '';
    }, [data.progressDetails, isFailed, isFromQueuedSection, isImport, t]);

    const helperChildContent = React.useCallback(() => {
        const title = helperChildContentTitle();
        const titleIcon = isFailed ? 'exclamation-triangle-outline' : 'info';
        const isError = isFailed;
        const helperTableProps = getHelperTableProps();
        if (title && shouldShowHelperData) {
            return { title, titleIcon, isError, helperTableProps };
        }
        if ((title && isFailed) || (isFromQueuedSection && !isImport)) {
            return { title, titleIcon, isError };
        }
        return null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [helperChildContentTitle, isFailed, shouldShowHelperData]);

    const getHelperData = React.useCallback(() => {
        const helperData = helperChildContent();
        if (helperData) {
            setIsHelperDataAvailable(true);
        } else {
            setIsHelperDataAvailable(false);
        }
        return helperData;
    }, [helperChildContent]);
    return { getHelperData, isHelperDataAvailable };
};

export default useGetSidePanelContentCardHelperData;
